// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import React, { useId } from 'react';

interface ProviderAccessSuccessIllustrationProps {
  /** Square size in px (width and height). Defaults to 100. */
  size?: number;
  className?: string;
  style?: React.CSSProperties;
  'aria-label'?: string;
}

/**
 * "Submission accepted" illustration — a 100×100 green check-in-circle
 * shown on the Provider Access success screen. Square aspect; scales
 * cleanly at any size via the {@link size} prop.
 */
export const ProviderAccessSuccessIllustration: React.FC<
  ProviderAccessSuccessIllustrationProps
> = ({
  size = 100,
  className,
  style,
  'aria-label': ariaLabel = 'Submission accepted',
}) => {
  const id = useId();
  const clipId = `pa-success-clip-${id}`;
  const maskId = `pa-success-mask-${id}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={ariaLabel}
      className={className}
      style={style}
    >
      <rect width="100" height="100" rx="50" fill="#00A63E" />
      <g clipPath={`url(#${clipId})`}>
        <mask
          id={maskId}
          style={{ maskType: 'luminance' }}
          maskUnits="userSpaceOnUse"
          x="22"
          y="22"
          width="56"
          height="56"
        >
          <path d="M78 22H22V78H78V22Z" fill="white" />
        </mask>
        <g mask={`url(#${maskId})`}>
          <path d="M49.9993 75.0832C36.1627 75.0832 24.916 63.8365 24.916 49.9998C24.916 36.1632 36.1627 24.9165 49.9993 24.9165C63.836 24.9165 75.0827 36.1632 75.0827 49.9998C75.0827 63.8365 63.836 75.0832 49.9993 75.0832ZM49.9993 28.4165C38.0993 28.4165 28.416 38.0998 28.416 49.9998C28.416 61.8998 38.0993 71.5832 49.9993 71.5832C61.8993 71.5832 71.5827 61.8998 71.5827 49.9998C71.5827 38.0998 61.8993 28.4165 49.9993 28.4165Z" fill="white" />
          <path d="M46.6853 58.3521C46.2186 58.3521 45.7753 58.1654 45.4486 57.8387L38.8454 51.2354C38.1687 50.5587 38.1687 49.4387 38.8454 48.7621C39.5221 48.0854 40.6421 48.0854 41.3187 48.7621L46.6853 54.1287L58.6786 42.1354C59.3553 41.4588 60.4753 41.4588 61.152 42.1354C61.8286 42.8121 61.8286 43.9321 61.152 44.6088L47.922 57.8387C47.5953 58.1654 47.152 58.3521 46.6853 58.3521Z" fill="white" />
        </g>
      </g>
      <defs>
        <clipPath id={clipId}>
          <rect width="56" height="56" fill="white" transform="translate(22 22)" />
        </clipPath>
      </defs>
    </svg>
  );
};
